/**
 * Shared walkthrough list item — used on both the project overview
 * and the walkthroughs page.
 *
 * Two layouts:
 *   - showScenario (dashboard): editorial. Scenario name as the headline,
 *     verdict-sentence as the dek. Different scenarios per row.
 *   - default (walkthroughs page, grouped under one scenario): tabular.
 *     Aligned columns — Run · Verdict · Findings · Time. The colored
 *     verdict word is the scan anchor.
 */

import { cn } from "@/lib/utils.js";
import {
  severityTextColorMap,
  SEVERITY_ORDER,
} from "@/components/finding-badges.js";
import {
  composeWalkthroughTone,
  getPrimaryVerdict,
  verdictLabel,
  verdictSentence,
  walkthroughStatusLabel,
  type Tone,
  type VerdictRating,
  type WalkthroughStatus,
} from "@/components/experience/vocabulary.js";
import { LoaderCircle } from "lucide-react";
import { Chip } from "@/components/ui/chip";
import { Timestamp } from "@/components/ui/timestamp";
import { toneChipTone } from "@/components/walkthrough-badges";
import type { ProblemCounts } from "@/lib/run-trend";

// ── Types ──────────────────────────────────────────────────────

export interface WalkthroughItemData {
  id: string;
  scenarioName?: string | null;
  personaName?: string | null;
  mode: string;
  status: string;
  verdict?: Array<{ rating: string; reasoning?: string }> | null;
  findingCount?: number | null;
  severityCounts?: Record<string, number> | null;
  problemCounts?: ProblemCounts | null;
  completedAt?: string | Date | null;
  startedAt?: string | null;
  createdAt: string | Date;
}

interface WalkthroughItemContentProps {
  walkthrough: WalkthroughItemData;
  /** Editorial layout with scenario name as headline (overview page) */
  showScenario?: boolean;
  /** Show persona name as the row identifier (multi-persona mode) */
  showPersona?: boolean;
  /** Override identifier (e.g. "Run #3") */
  label?: string;
  /** @deprecated mode tag is no longer rendered (single-mode product) */
  hideMode?: boolean;
}

// ── Main component ─────────────────────────────────────────────

export function WalkthroughItemContent({
  walkthrough: wt,
  showScenario = false,
  showPersona = true,
  label,
}: WalkthroughItemContentProps) {
  const dateStr = wt.completedAt ?? wt.startedAt ?? wt.createdAt;
  const primaryRating = getPrimaryVerdict(
    wt.verdict as Array<{ rating: VerdictRating }> | null,
  );

  const tone = composeWalkthroughTone(wt.status, primaryRating);
  const verdictWord = composeVerdictWord(wt.status, primaryRating);
  const findingsSummary =
    wt.status === "failed"
      ? "Execution aborted"
      : wt.status === "running"
        ? "In progress"
        : wt.status === "pending"
          ? "Queued"
          : composeFindingsSummary(
              wt.findingCount ?? 0,
              wt.severityCounts ?? {},
              wt.problemCounts,
            );

  if (showScenario) {
    return (
      <EditorialLayout
        scenarioName={wt.scenarioName ?? "Walkthrough"}
        personaName={wt.personaName ?? null}
        verdictSentence={composeVerdictSentence(wt.status, primaryRating)}
        findingsSummary={findingsSummary}
        verdictWord={verdictWord}
        tone={tone}
        date={dateStr}
      />
    );
  }

  const identifier =
    label ?? (showPersona && wt.personaName ? wt.personaName : "Run");

  return (
    <TabularLayout
      identifier={identifier}
      verdictWord={verdictWord}
      findingsSummary={findingsSummary}
      tone={tone}
      date={dateStr}
    />
  );
}

// ── Layouts ────────────────────────────────────────────────────

function TabularLayout({
  identifier,
  verdictWord,
  findingsSummary,
  tone,
  date,
}: {
  identifier: string;
  verdictWord: string;
  findingsSummary: string;
  tone: Tone;
  date: string | Date;
}) {
  return (
    <div className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4">
      <StatusDot tone={tone} />
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 sm:grid-cols-[minmax(9rem,14rem)_6.5rem_minmax(0,1fr)] sm:gap-x-4">
        <span
          className="truncate text-base font-medium text-foreground"
          title={identifier}
        >
          {identifier}
        </span>
        <span>
          <Chip tone={toneChipTone(tone)}>{verdictWord}</Chip>
        </span>
        <span className="col-span-2 truncate text-sm tabular-nums text-muted-foreground sm:col-span-1">
          {findingsSummary}
        </span>
      </div>
      <Timestamp
        date={date}
        className="w-16 text-right text-xs tabular-nums text-muted-foreground"
      />
    </div>
  );
}

function EditorialLayout({
  scenarioName,
  personaName,
  verdictSentence,
  findingsSummary,
  verdictWord,
  tone,
  date,
}: {
  scenarioName: string;
  personaName: string | null;
  verdictSentence: string;
  findingsSummary: string;
  verdictWord: string;
  tone: Tone;
  date: string | Date;
}) {
  return (
    <div className="flex w-full min-w-0 items-start gap-3">
      <span
        className="mt-1.5 flex shrink-0 items-center justify-center"
        aria-hidden="true"
      >
        <StatusDot tone={tone} />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-baseline gap-4">
          <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">
            {scenarioName}
          </h3>
          <Timestamp
            date={date}
            className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
          />
        </div>
        <p className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
          <Chip tone={toneChipTone(tone)}>{verdictWord}</Chip>
          <span className="truncate">
            {findingsSummary
              ? `${verdictSentence} — ${findingsSummary}`
              : verdictSentence}
            {personaName ? `, as ${personaName}` : ""}
          </span>
        </p>
      </div>
    </div>
  );
}

// ── Status dot ────────────────────────────────────────────────

function StatusDot({ tone }: { tone: Tone }) {
  if (tone === "running") {
    return (
      <LoaderCircle
        className="h-3 w-3 animate-spin text-primary"
        aria-hidden="true"
      />
    );
  }
  const isQuiet = tone === "muted" || tone === "helps";
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block rounded-full",
        isQuiet ? "size-1.5" : "size-2",
        tone === "helps" && "bg-mark-helps",
        tone === "critical" && "bg-mark-critical",
        tone === "important" && "bg-mark-important",
        tone === "muted" && "bg-muted-foreground/40",
      )}
    />
  );
}

// ── Composition helpers ───────────────────────────────────────

/**
 * A walkthrough's headline sentence folds in non-terminal statuses
 * (running/pending/failed) that the bare verdict vocabulary doesn't
 * cover. Verdict sentences come from vocabulary; this helper just
 * handles the status branches.
 */
function composeVerdictSentence(
  status: WalkthroughStatus,
  rating: VerdictRating | null,
): string {
  if (status === "failed") return "Run failed";
  if (status === "running") return "Run in progress";
  if (status === "pending") return "Pending";
  if (rating) return verdictSentence(rating);
  return "Run completed";
}

export function composeVerdictWord(
  status: WalkthroughStatus,
  rating: VerdictRating | null,
): string {
  if (status === "failed") return "Runner error";
  if (status === "running") return "Running";
  if (status === "pending") return "Pending";
  if (rating) return verdictLabel(rating);
  return "No verdict";
}

export function composeFindingsSummary(
  count: number,
  severityCounts: Record<string, number>,
  problemCounts?: ProblemCounts | null,
): string {
  if (count === 0) return "no problems";

  const critical = severityCounts.critical ?? 0;
  const major = severityCounts.major ?? 0;
  const minor = severityCounts.minor ?? 0;
  const helps = severityCounts.helps ?? severityCounts.strength ?? 0;

  const problemSevParts: string[] = [];
  if (critical > 0) problemSevParts.push(`${critical} critical`);
  if (major > 0) problemSevParts.push(`${major} major`);
  if (minor > 0) problemSevParts.push(`${minor} minor`);

  const calculatedProblems = critical + major + minor;
  const totalProblems = problemCounts?.found ?? calculatedProblems;

  const sections: string[] = [];

  if (totalProblems > 0) {
    const breakdown =
      problemSevParts.length > 0 ? ` (${problemSevParts.join(" · ")})` : "";
    sections.push(
      `${totalProblems} ${totalProblems === 1 ? "problem" : "problems"}${breakdown}`,
    );
  } else if (helps > 0) {
    sections.push("no problems");
  }

  if (helps > 0) {
    sections.push(`${helps} ${helps === 1 ? "strength" : "strengths"}`);
  }

  if (sections.length === 0) {
    return `${count} ${count === 1 ? "finding" : "findings"}`;
  }

  return sections.join(" · ");
}

// ── Backward-compat exports ───────────────────────────────────

export function FindingSummary({
  count,
  severityCounts,
  problemCounts,
}: {
  count: number;
  severityCounts: Record<string, number>;
  problemCounts?: ProblemCounts | null;
}) {
  if (count === 0) {
    return <span className="text-muted-foreground shrink-0">No problems</span>;
  }

  const critical = severityCounts.critical ?? 0;
  const major = severityCounts.major ?? 0;
  const minor = severityCounts.minor ?? 0;
  const helps = severityCounts.helps ?? severityCounts.strength ?? 0;

  const problemSevParts: Array<{ severity: string; count: number }> = [];
  if (critical > 0)
    problemSevParts.push({ severity: "critical", count: critical });
  if (major > 0) problemSevParts.push({ severity: "major", count: major });
  if (minor > 0) problemSevParts.push({ severity: "minor", count: minor });

  const calculatedProblems = critical + major + minor;
  const totalProblems = problemCounts?.found ?? calculatedProblems;

  if (totalProblems === 0 && helps === 0) {
    return (
      <span className="shrink-0">
        {count} {count === 1 ? "finding" : "findings"}
      </span>
    );
  }

  return (
    <span
      role="group"
      aria-label="Finding severity breakdown"
      className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 shrink-0"
    >
      {totalProblems > 0 ? (
        <>
          <span className="font-medium text-foreground">
            {totalProblems} {totalProblems === 1 ? "problem" : "problems"}
          </span>
          {problemSevParts.length > 0 && (
            <span className="text-muted-foreground">
              (
              {problemSevParts.map(({ severity, count: n }, i) => (
                <span key={severity}>
                  {i > 0 && " · "}
                  <span
                    className={cn(
                      "font-medium whitespace-nowrap",
                      severityTextColorMap[
                        severity as keyof typeof severityTextColorMap
                      ],
                    )}
                  >
                    {n} {severity}
                  </span>
                </span>
              ))}
              )
            </span>
          )}
        </>
      ) : (
        <span className="text-muted-foreground">No problems</span>
      )}
      {helps > 0 && (
        <>
          {totalProblems > 0 && (
            <span className="text-muted-foreground">·</span>
          )}
          <span className="font-medium whitespace-nowrap text-mark-helps">
            {helps} {helps === 1 ? "strength" : "strengths"}
          </span>
        </>
      )}
    </span>
  );
}

export { formatRelativeTime as relativeTime } from "@/lib/freshness";
